import React, {useState, useEffect} from 'react';
import Modal from './Modal';
import {FaRegCopy, FaCheck} from 'react-icons/fa';
import '../styles/SSHConnectionModal.scss';

interface SSHConnectionModalProps {
    isOpen: boolean;
    onClose: () => void;
    sshCommand: string;
    sshUser: string;
    host: string;
    port: string;
    onCopy: () => void;
}

// Works in both HTTPS and HTTP/localhost
function copyToClipboard(text: string): Promise<void> {
    if (navigator.clipboard && window.isSecureContext) {
        return navigator.clipboard.writeText(text);
    }
    const el = document.createElement('textarea');
    el.value = text;
    el.style.position = 'fixed';
    el.style.opacity = '0';
    document.body.appendChild(el);
    el.focus();
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
    return Promise.resolve();
}

const CommandBox: React.FC<{ command: string; onCopy: () => void }> = ({command, onCopy}) => {
    const [copied, setCopied] = useState(false);

    const handleInternalCopy = () => {
        copyToClipboard(command).then(() => {
            setCopied(true);
            onCopy();
        }).catch((_err) => { /* silent */ });
    };

    useEffect(() => {
        if (copied) {
            const timeout = setTimeout(() => setCopied(false), 2000);
            return () => clearTimeout(timeout);
        }
    }, [copied]);

    return (
        <div className="command-block">
            <code>{command}</code>
            <button
                className={`copy-inner-btn ${copied ? 'copied' : ''}`}
                onClick={handleInternalCopy}
                title="Copy to clipboard"
            >
                {copied ? <FaCheck className="icon-check"/> : <FaRegCopy className="icon-copy"/>}
            </button>
        </div>
    );
};

const SSHConnectionModal: React.FC<SSHConnectionModalProps> = ({
    isOpen, onClose, sshCommand, sshUser, host, port, onCopy
}) => {
    return (
        <Modal isOpen={isOpen} onClose={onClose} title="SSH Connection Guide" size="lg">
            <div className="ssh-modal-container">
                <div className="ssh-section">
                    <h3>Linux / macOS</h3>
                    <p>Open your terminal and run the following command:</p>
                    <CommandBox command={sshCommand} onCopy={onCopy}/>
                </div>

                <div className="ssh-section">
                    <h3>Windows (PowerShell / CMD)</h3>
                    <p>Open PowerShell or Command Prompt and run:</p>
                    <CommandBox command={sshCommand} onCopy={onCopy}/>
                </div>

                <div className="ssh-section">
                    <h3>Windows (PuTTY)</h3>
                    <div className="putty-info">
                        <p>If you are using <strong>PuTTY</strong>, use these settings:</p>
                        <ul>
                            <li><strong>Host Name:</strong> {host}</li>
                            <li><strong>Port:</strong> {port}</li>
                            <li><strong>User:</strong> {sshUser}</li>
                        </ul>
                    </div>
                </div>

                <div className="ssh-modal-footer">
                    <button className="close-button-alt" onClick={onClose}>Close</button>
                </div>
            </div>
        </Modal>
    );
};

export default SSHConnectionModal;
